/**
 * Headcount Canonical Model
 * 
 * Distinguishes between Total Employees (company-wide) and Subcategory counts.
 * Prevents incorrect summation of subsets.
 * 
 * Schema:
 * headcount: {
 *   total?: { value: number; period?: string|null; source: "Document"|"Model"|"Calculated"; evidence?: EvidenceRef[] }
 *   byFunction?: Array<{ function: string; count: number; source: "Document"|"Model"; evidence?: EvidenceRef[]; isSubsetOfTotal: true }>
 *   otherEntities?: Array<{ label: string; count: number; reason: string; evidence?: EvidenceRef[] }> // only if explicitly separate
 *   ambiguity?: {
 *     multipleTotalsFound: boolean
 *     candidates?: Array<{ value: number; period?: string|null; evidence?: EvidenceRef[] }>
 *     resolutionRule?: string
 *   }
 * }
 */

/**
 * Evidence reference structure
 * @typedef {Object} EvidenceRef
 * @property {string} [docId]
 * @property {string} [snippet]
 * @property {number} [page]
 */

/**
 * Total headcount entry
 * @typedef {Object} HeadcountTotal
 * @property {number} value
 * @property {string|null} [period]
 * @property {"Document"|"Model"|"Calculated"} source
 * @property {EvidenceRef[]} [evidence]
 */

/**
 * Function/subcategory entry
 * @typedef {Object} HeadcountByFunction
 * @property {string} function
 * @property {number} count
 * @property {"Document"|"Model"} source
 * @property {EvidenceRef[]} [evidence]
 * @property {boolean} isSubsetOfTotal
 */

/**
 * Other entity (contractors, separate legal entities)
 * @typedef {Object} OtherEntity
 * @property {string} label
 * @property {number} count
 * @property {string} reason
 * @property {EvidenceRef[]} [evidence]
 */

/**
 * Ambiguity info when multiple totals found
 * @typedef {Object} HeadcountAmbiguity
 * @property {boolean} multipleTotalsFound
 * @property {Array<{value: number, period?: string|null, evidence?: EvidenceRef[]}>} [candidates]
 * @property {string} [resolutionRule]
 */

/**
 * Complete headcount structure
 * @typedef {Object} HeadcountModel
 * @property {HeadcountTotal} [total]
 * @property {HeadcountByFunction[]} [byFunction]
 * @property {OtherEntity[]} [otherEntities]
 * @property {HeadcountAmbiguity} [ambiguity]
 */

/**
 * Labels that indicate a TOTAL headcount (company-wide)
 */
export const TOTAL_LABELS = [
  'total',
  'total employees',
  'total headcount',
  'headcount',
  'employees',
  'fte',
  'full time employees',
  'full-time employees',
  'company-wide',
  'company wide',
  'workforce',
  'staff'
];

/**
 * Labels that indicate SUBCATEGORIES (roles/functions)
 */
export const SUBCATEGORY_LABELS = [
  'sales',
  'operations',
  'ops',
  'admin',
  'administrative',
  'engineering',
  'engineers',
  'field engineers',
  'technicians',
  'techs',
  'management',
  'managers',
  'support',
  'customer service',
  'production',
  'manufacturing',
  'warehouse',
  'logistics',
  'drivers',
  'installers',
  'service',
  'field',
  'office',
  'back office',
  'finance',
  'accounting',
  'hr',
  'human resources',
  'it',
  'marketing',
  'business development',
  'bd',
  'project managers',
  'pm',
  'estimators',
  'dispatchers'
];

/**
 * Labels that indicate CONTRACTORS (separate from employees)
 */
export const CONTRACTOR_LABELS = [
  'contractors',
  'contractor',
  ' subcontractors',
  'subcontractor',
  'temps',
  'temporary',
  'contingent',
  '1099',
  'w-9',
  'w9'
];

/**
 * Determine if a label indicates a total headcount
 * @param {string} label
 * @returns {boolean}
 */
export function isTotalLabel(label) {
  if (!label) return false;
  const normalized = label.toLowerCase().trim();
  return TOTAL_LABELS.some(l => normalized.includes(l));
}

/**
 * Determine if a label indicates a subcategory
 * @param {string} label
 * @returns {boolean}
 */
export function isSubcategoryLabel(label) {
  if (!label) return false;
  const normalized = label.toLowerCase().trim();
  return SUBCATEGORY_LABELS.some(l => normalized.includes(l));
}

/**
 * Determine if a label indicates contractors
 * @param {string} label
 * @returns {boolean}
 */
export function isContractorLabel(label) {
  if (!label) return false;
  const normalized = label.toLowerCase().trim();
  return CONTRACTOR_LABELS.some(l => normalized.includes(l));
}

/**
 * Create an empty headcount model
 * @returns {HeadcountModel}
 */
export function createEmptyHeadcount() {
  return {
    total: undefined,
    byFunction: [],
    otherEntities: [],
    ambiguity: undefined
  };
}

/**
 * Get the display total for headcount
 * Uses headcount.total.value if available, otherwise handles subsets appropriately
 * @param {HeadcountModel} headcount
 * @returns {number|null} The total headcount to display
 */
export function getHeadcountTotal(headcount) {
  if (!headcount) return null;
  
  // If we have an explicit total, use it
  if (headcount.total?.value != null) {
    return headcount.total.value;
  }
  
  // If no total, check if we should sum subcategories
  // Only sum if explicitly marked as representing full workforce
  if (headcount.byFunction?.length > 0) {
    const allMarkedAsComplete = headcount.byFunction.every(f => 
      f.isCompleteWorkforce || f.representsFullWorkforce
    );
    
    if (allMarkedAsComplete) {
      return headcount.byFunction.reduce((sum, f) => sum + (f.count || 0), 0);
    }
  }
  
  return null;
}

/**
 * Get headcount display with warning info
 * @param {HeadcountModel} headcount
 * @returns {{value: number|null, warning: string|null, isSubsetSum: boolean, hasAmbiguity: boolean}}
 */
export function getHeadcountDisplayInfo(headcount) {
  if (!headcount) {
    return { value: null, warning: null, isSubsetSum: false, hasAmbiguity: false };
  }
  
  const hasAmbiguity = headcount.ambiguity?.multipleTotalsFound || false;
  
  // If we have an explicit total
  if (headcount.total?.value != null) {
    // Check if sum of subsets exceeds total by > 5%
    if (headcount.byFunction?.length > 0) {
      const subsetSum = headcount.byFunction.reduce((sum, f) => sum + (f.count || 0), 0);
      if (subsetSum > headcount.total.value * 1.05) {
        return {
          value: headcount.total.value,
          warning: 'Breakdown exceeds total; likely mixed definitions',
          isSubsetSum: false,
          hasAmbiguity
        };
      }
    }
    
    return {
      value: headcount.total.value,
      warning: hasAmbiguity ? 'Multiple totals found' : null,
      isSubsetSum: false,
      hasAmbiguity
    };
  }
  
  // No explicit total
  if (headcount.byFunction?.length > 0) {
    const allMarkedAsComplete = headcount.byFunction.every(f => 
      f.isCompleteWorkforce || f.representsFullWorkforce
    );
    
    if (allMarkedAsComplete) {
      const sum = headcount.byFunction.reduce((s, f) => s + (f.count || 0), 0);
      return {
        value: sum,
        warning: null,
        isSubsetSum: true,
        hasAmbiguity: false
      };
    }
    
    return {
      value: null,
      warning: 'Total headcount not provided',
      isSubsetSum: false,
      hasAmbiguity: false
    };
  }
  
  return { value: null, warning: null, isSubsetSum: false, hasAmbiguity: false };
}

/**
 * Check if headcount has ambiguity (multiple totals found)
 * @param {HeadcountModel} headcount
 * @returns {boolean}
 */
export function hasAmbiguousHeadcount(headcount) {
  return headcount?.ambiguity?.multipleTotalsFound === true;
}

/**
 * Get ambiguity candidates for display
 * @param {HeadcountModel} headcount
 * @returns {Array<{value: number, period?: string|null}>|null}
 */
export function getAmbiguityCandidates(headcount) {
  if (!hasAmbiguousHeadcount(headcount)) return null;
  return headcount.ambiguity?.candidates || null;
}

export default {
  isTotalLabel,
  isSubcategoryLabel,
  isContractorLabel,
  createEmptyHeadcount,
  getHeadcountTotal,
  getHeadcountDisplayInfo,
  hasAmbiguousHeadcount,
  getAmbiguityCandidates,
  TOTAL_LABELS,
  SUBCATEGORY_LABELS,
  CONTRACTOR_LABELS
};
